import { apiClient } from "@/lib/apiClient";
import type {
  Category,
  GetCategoriesParams,
  GetCategoriesResponse,
  CreateCategoryPayload,
  UpdateCategoryPayload,
} from "../types/categories.types";

const LIST_ENDPOINT = "/api/education-categories";
const ADMIN_ENDPOINT = "/api/admin/education-categories";

export const categoriesApi = {
  /**
   * Tümünü listele (flat). Tablo için; parentId gönderilmezse karışık tüm kategoriler.
   * Query: page, limit, search, isActive, parentId (opsiyonel).
   */
  async getList(
    params: GetCategoriesParams = {}
  ): Promise<GetCategoriesResponse> {
    const { page = 1, limit = 20, search, isActive, parentId } = params;
    const cappedLimit = Math.min(Number(limit) || 20, 100);
    const query: Record<string, string | number | boolean | undefined> = {
      page,
      limit: cappedLimit,
    };
    if (search !== undefined && search !== "") query.search = search;
    if (isActive !== undefined) query.isActive = isActive;
    if (parentId !== undefined) {
      query.parentId = parentId === null ? "null" : parentId;
    }
    const { data } = await apiClient.get<GetCategoriesResponse>(LIST_ENDPOINT, {
      params: query,
    });
    const res = data as unknown as GetCategoriesResponse & {
      data?: (Category & { parentID?: string | null })[];
      items?: (Category & { parentID?: string | null })[];
      total?: number;
      totalCount?: number;
      count?: number;
    };
    type ApiCategory = Category & { parentID?: string | null };
    const rawList = (res.data ?? res.items ?? []) as ApiCategory[];
    const list = Array.isArray(rawList)
      ? rawList.map((item) => ({
          ...item,
          parentId: item.parentId ?? item.parentID ?? null,
        }))
      : [];
    const total = res.total ?? res.totalCount ?? res.count ?? list.length;
    return { data: list, total: Number(total) };
  },

  /**
   * Sadece root kategorileri getir (dropdown için: üst kategori = main category).
   * API limit max 100.
   */
  async getRootCategories(): Promise<Category[]> {
    const res = await this.getList({ parentId: null, limit: 100 });
    return res.data ?? [];
  },

  /**
   * Tekil kategori detayı.
   */
  async getById(id: string): Promise<Category> {
    const { data } = await apiClient.get<Category>(`${ADMIN_ENDPOINT}/${id}`);
    return data as Category;
  },

  /**
   * Yeni kategori oluştur. parentId boş/null = root; dolu = alt kategori (dropdown'dan seçilen main category).
   */
  async create(payload: CreateCategoryPayload): Promise<Category> {
    const body: Record<string, unknown> = {
      name: payload.name,
      isActive: payload.isActive ?? true,
    };
    if (payload.description !== undefined && payload.description !== null)
      body.description = payload.description;
    if (payload.parentId !== undefined && payload.parentId !== null && payload.parentId !== "")
      body.parentId = payload.parentId;
    const { data } = await apiClient.post<Category>(ADMIN_ENDPOINT, body);
    return data as Category;
  },

  /**
   * Kategori güncelle.
   */
  async update(id: string, payload: UpdateCategoryPayload): Promise<Category> {
    const { data } = await apiClient.patch<Category>(
      `${ADMIN_ENDPOINT}/${id}`,
      payload
    );
    return data as Category;
  },

  /**
   * Kategori sil.
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${ADMIN_ENDPOINT}/${id}`);
  },
};
